import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { AuthContext } from '../App';
import ContentManager from '../components/ContentManager';
import { 
  LayoutDashboard, Users, FileText, BarChart3, BookOpen, Menu, X, LogOut,
  TrendingUp, Activity, Search, Trash2, Eye, Calendar, Mail, Phone,
  Globe, Award, Flame, CheckCircle, AlertTriangle, UserX, RefreshCw,
  ChevronRight, Star, Zap, Target, Crown, Settings, Briefcase, Building2,
  Newspaper, Edit, Plus, Save, XCircle, Filter, Download, Upload,
  UserCheck, Clock, MapPin, GraduationCap, Heart, MessageSquare, Mic,
  PieChart, LineChart, TrendingDown, ArrowUpRight, ArrowDownRight,
  Bell, Shield, Database, Palette, Languages, UserPlus, FileSpreadsheet,
  ClipboardList, History, Key, ToggleLeft, ToggleRight, Send, Copy,
  Megaphone, MessageCircle, FileDown, BarChart2, Users2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin Sidebar Component
function AdminSidebar({ activeTab, setActiveTab, sidebarOpen, setSidebarOpen, onLogout }) {
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, color: 'text-blue-500' },
    { id: 'registrations', label: 'Kayıt Talepleri', icon: UserPlus, color: 'text-red-500', badge: true },
    { id: 'students', label: 'Öğrenci Yönetimi', icon: Users, color: 'text-green-500' },
    { id: 'demo', label: 'Demo Öğrenci', icon: Clock, color: 'text-yellow-500' },
    { id: 'lms', label: 'LMS Sistemi', icon: GraduationCap, color: 'text-purple-500' },
    { id: 'content', label: 'İçerik Yönetimi', icon: BookOpen, color: 'text-cyan-500' },
    { id: 'announcements', label: 'Duyurular', icon: Megaphone, color: 'text-amber-500' },
    { id: 'messages', label: 'Mesajlar', icon: MessageCircle, color: 'text-pink-500' },
    { id: 'reports', label: 'Raporlar', icon: BarChart3, color: 'text-indigo-500' },
    { id: 'logs', label: 'Aktivite Logları', icon: History, color: 'text-orange-500' },
    { id: 'settings', label: 'Ayarlar', icon: Settings, color: 'text-gray-500' },
  ];

  return (
    <>
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed top-0 left-0 z-50 h-full bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 transition-all duration-300 shadow-2xl ${sidebarOpen ? 'w-64' : 'w-0 lg:w-20'} overflow-hidden`}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
          <div className={`flex items-center gap-3 ${!sidebarOpen && 'lg:justify-center lg:w-full'}`}>
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
              <Crown className="w-6 h-6 text-white" />
            </div>
            {sidebarOpen && <div><h1 className="font-bold text-white text-lg">Admin Panel</h1><p className="text-xs text-slate-400">LMS Pro</p></div>}
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-2 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100%-8rem)]">
          {menuItems.map((item) => (
            <button key={item.id} onClick={() => { setActiveTab(item.id); if (window.innerWidth < 1024) setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${activeTab === item.id ? 'bg-gradient-to-r from-amber-600 to-red-600 text-white shadow-lg' : 'text-slate-300 hover:bg-slate-700/50'} ${!sidebarOpen && 'lg:justify-center lg:px-2'}`}>
              <item.icon className={`w-5 h-5 ${activeTab !== item.id ? item.color : ''}`} />
              {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
            </button>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-700 bg-slate-900/50">
          <button onClick={onLogout} className={`w-full flex items-center gap-2 px-3 py-2 text-slate-400 hover:text-red-400 rounded-lg hover:bg-slate-800 ${!sidebarOpen && 'lg:justify-center'}`}>
            <LogOut className="w-5 h-5" />{sidebarOpen && <span className="text-sm">Çıkış</span>}
          </button>
        </div>
      </aside>
    </>
  );
}

// Stats Card
function StatsCard({ title, value, icon: Icon, color, trend, trendValue, subtitle, onClick }) {
  return (
    <Card className={`border-2 ${color} hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer`} onClick={onClick}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-3xl font-black text-gray-800 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
            {trend && <div className={`flex items-center gap-1 mt-2 text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>{trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}<span>{trendValue}</span></div>}
          </div>
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${color.replace('border-', 'bg-').replace('-200', '-500')}`}><Icon className="w-7 h-7 text-white" /></div>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Admin Component
export default function Admin() {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data states
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [lmsStats, setLmsStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reportsData, setReportsData] = useState(null);
  const [examReports, setExamReports] = useState(null);
  const [questions, setQuestions] = useState([]);
  
  // Modal states
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showStudentDetailModal, setShowStudentDetailModal] = useState(false);
  const [showAssignCourseModal, setShowAssignCourseModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [showEditQuestionModal, setShowEditQuestionModal] = useState(false);
  const [showBulkMessageModal, setShowBulkMessageModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  
  // Registration & Demo states
  const [registrationRequests, setRegistrationRequests] = useState([]);
  const [demoUsers, setDemoUsers] = useState([]);
  const [demoInterests, setDemoInterests] = useState([]);
  const [newDemoUser, setNewDemoUser] = useState({ email: '', first_name: '', last_name: '', password: 'demo123' });
  const [approvePassword, setApprovePassword] = useState('skylingo123');
  
  // Selected items
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedContentType, setSelectedContentType] = useState(null);
  const [selectedStudentsForBulk, setSelectedStudentsForBulk] = useState([]);
  const [cabinCrewModules, setCabinCrewModules] = useState([]);
  const [selectedCabinModule, setSelectedCabinModule] = useState(null);
  const [cabinModuleContent, setCabinModuleContent] = useState(null);
  
  // Form states
  const [newStudent, setNewStudent] = useState({ name: '', email: '', phone: '', password: 'student123' });
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', content: '', priority: 'normal', target_audience: 'all' });
  const [newMessage, setNewMessage] = useState({ recipient_id: '', subject: '', content: '' });
  const [bulkMessage, setBulkMessage] = useState({ subject: '', content: '' });
  const [newQuestion, setNewQuestion] = useState({ question_text: '', question_type: 'multiple_choice', options: ['', '', '', ''], correct_answer: '', difficulty: 'medium', category: 'grammar', module: 'comprehensive' });
  const [editingQuestion, setEditingQuestion] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Load data on mount and tab change
  useEffect(() => { loadAdminData(); loadCabinCrewModules(); }, []);
  
  // Load tab-specific data when tab changes
  useEffect(() => {
    if (activeTab === 'announcements') loadAnnouncements();
    else if (activeTab === 'messages') loadMessages();
    else if (activeTab === 'reports') loadReports();
    else if (activeTab === 'logs') loadActivityLogs();
    else if (activeTab === 'registrations') loadRegistrationRequests();
    else if (activeTab === 'demo') loadDemoUsers();
  }, [activeTab]);

  const loadAdminData = async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, usersRes, lmsRes, coursesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true, headers }),
        axios.get(`${API}/admin/users`, { withCredentials: true, headers }),
        axios.get(`${API}/admin/lms/stats`, { withCredentials: true, headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/courses`, { withCredentials: true, headers }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setLmsStats(lmsRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      if (error.response?.status === 403) { toast.error('Admin yetkisi gerekli'); navigate('/dashboard'); }
      else toast.error('Veriler yüklenemedi');
    } finally { setLoading(false); }
  };

  const loadAnnouncements = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/announcements`, { withCredentials: true, headers });
      setAnnouncements(response.data);
    } catch (error) { console.error('Announcements fetch error:', error); }
  };

  const loadRegistrationRequests = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/registration-requests`, { withCredentials: true, headers });
      setRegistrationRequests(response.data);
    } catch (error) { console.error('Registration requests fetch error:', error); }
  };

  const loadDemoUsers = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true, headers });
      setDemoUsers(response.data.filter(u => u.is_demo));
      // Also load demo interests
      const interestsRes = await axios.get(`${API}/admin/demo-interests`, { withCredentials: true, headers });
      setDemoInterests(interestsRes.data || []);
    } catch (error) { console.error('Demo users fetch error:', error); }
  };

  const handleApproveRegistration = async (requestId, isDemo = false) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/registration-requests/${requestId}/approve`, { password: approvePassword, is_demo: isDemo }, { withCredentials: true, headers });
      toast.success(isDemo ? 'Demo kullanıcı oluşturuldu!' : 'Kullanıcı onaylandı!');
      loadRegistrationRequests();
    } catch (error) { toast.error(error.response?.data?.detail || 'Onay başarısız'); }
  };

  const handleRejectRegistration = async (requestId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/registration-requests/${requestId}/reject`, {}, { withCredentials: true, headers });
      toast.success('Talep reddedildi');
      loadRegistrationRequests();
    } catch (error) { toast.error('Reddetme başarısız'); }
  };

  const handleCreateDemoUser = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/admin/create-demo-user`, newDemoUser, { withCredentials: true, headers });
      toast.success(`Demo kullanıcı oluşturuldu! E-posta: ${response.data.email}, Şifre: ${response.data.password}`);
      setShowDemoModal(false);
      setNewDemoUser({ email: '', first_name: '', last_name: '', password: 'demo123' });
      loadDemoUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Demo kullanıcı oluşturulamadı'); }
  };

  const handleExtendDemo = async (userId, hours = 8) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/extend-demo/${userId}`, { hours }, { withCredentials: true, headers });
      toast.success(`Demo süresi ${hours} saat uzatıldı`);
      loadDemoUsers();
    } catch (error) { toast.error('Süre uzatılamadı'); }
  };

  const handleConvertToFull = async (userId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/convert-to-full/${userId}`, {}, { withCredentials: true, headers });
      toast.success('Kullanıcı tam sürüme geçirildi');
      loadDemoUsers();
      loadAdminData();
    } catch (error) { toast.error('Dönüştürme başarısız'); }
  };

  const loadMessages = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/messages`, { withCredentials: true, headers });
      setMessages(response.data);
    } catch (error) { console.error('Messages fetch error:', error); }
  };

  const loadActivityLogs = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/logs?limit=100`, { withCredentials: true, headers });
      setActivityLogs(response.data);
    } catch (error) { console.error('Logs fetch error:', error); }
  };

  const loadReports = async () => {
    try {
      const headers = getAuthHeaders();
      const [overviewRes, examsRes] = await Promise.all([
        axios.get(`${API}/admin/reports/overview`, { withCredentials: true, headers }),
        axios.get(`${API}/admin/reports/exams`, { withCredentials: true, headers })
      ]);
      setReportsData(overviewRes.data);
      setExamReports(examsRes.data);
    } catch (error) { console.error('Reports fetch error:', error); }
  };

  const loadQuestions = async (category = null) => {
    try {
      const headers = getAuthHeaders();
      let url = `${API}/admin/questions`;
      if (category) url += `?category=${category}`;
      const response = await axios.get(url, { withCredentials: true, headers });
      setQuestions(response.data);
    } catch (error) { toast.error('Sorular yüklenemedi'); }
  };

  const loadCabinCrewModules = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/cabin-crew-modules`, { withCredentials: true, headers });
      setCabinCrewModules(response.data);
    } catch (error) { console.error('Cabin crew modules error:', error); }
  };

  const loadCabinModuleContent = async (moduleId) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/cabin-crew-content/${moduleId}`, { withCredentials: true, headers });
      setCabinModuleContent(response.data);
    } catch (error) { toast.error('İçerik yüklenemedi'); }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('session_token');
      setUser(null);
      toast.success('Çıkış yapıldı');
      navigate('/');
    } catch (error) { toast.error('Çıkış yapılamadı'); }
  };

  // Student operations
  const handleAddStudent = async () => {
    if (!newStudent.name || !newStudent.email) { toast.error('İsim ve e-posta zorunludur'); return; }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/students/register`, newStudent, { withCredentials: true, headers });
      toast.success('Öğrenci eklendi');
      setShowAddStudentModal(false);
      setNewStudent({ name: '', email: '', phone: '', password: 'student123' });
      loadAdminData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Öğrenci eklenemedi'); }
  };

  const handleViewStudent = async (student) => {
    setSelectedStudent(student);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/students/${student.id}/details`, { withCredentials: true, headers });
      setStudentDetails(response.data);
      setShowStudentDetailModal(true);
    } catch (error) { toast.error('Detaylar yüklenemedi'); }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('Bu öğrenciyi silmek istediğinize emin misiniz?')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/users/${studentId}`, { withCredentials: true, headers });
      toast.success('Öğrenci silindi');
      setUsers(users.filter(u => u.id !== studentId));
    } catch (error) { toast.error('Silinemedi'); }
  };

  const handleToggleStatus = async (studentId) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/admin/students/${studentId}/toggle-status`, {}, { withCredentials: true, headers });
      toast.success(response.data.message);
      loadAdminData();
    } catch (error) { toast.error('Durum güncellenemedi'); }
  };

  const handleResetPassword = async (studentId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/students/${studentId}/reset-password`, {}, { withCredentials: true, headers });
      toast.success('Şifre sıfırlandı: student123');
    } catch (error) { toast.error('Şifre sıfırlanamadı'); }
  };

  const handleAssignCourses = async () => {
    if (!selectedStudent || selectedCourses.length === 0) return;
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/students/${selectedStudent.id}/assign-courses`, { student_id: selectedStudent.id, course_ids: selectedCourses }, { withCredentials: true, headers });
      toast.success('Dersler atandı');
      setShowAssignCourseModal(false);
      setSelectedCourses([]);
    } catch (error) { toast.error('Atama başarısız'); }
  };

  // Announcement operations
  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.content) { toast.error('Başlık ve içerik zorunludur'); return; }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/announcements`, newAnnouncement, { withCredentials: true, headers });
      toast.success('Duyuru oluşturuldu');
      setShowAnnouncementModal(false);
      setNewAnnouncement({ title: '', content: '', priority: 'normal', target_audience: 'all' });
      loadAnnouncements();
    } catch (error) { toast.error('Duyuru oluşturulamadı'); }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/announcements/${id}`, { withCredentials: true, headers });
      toast.success('Duyuru silindi');
      loadAnnouncements();
    } catch (error) { toast.error('Silinemedi'); }
  };

  // Message operations
  const handleSendMessage = async () => {
    if (!newMessage.recipient_id || !newMessage.subject || !newMessage.content) { toast.error('Tüm alanlar zorunludur'); return; }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/messages`, newMessage, { withCredentials: true, headers });
      toast.success('Mesaj gönderildi');
      setShowMessageModal(false);
      setNewMessage({ recipient_id: '', subject: '', content: '' });
      loadMessages();
    } catch (error) { toast.error('Mesaj gönderilemedi'); }
  };

  const handleSendBulkMessage = async () => {
    if (selectedStudentsForBulk.length === 0 || !bulkMessage.subject || !bulkMessage.content) { toast.error('Alıcı ve içerik zorunludur'); return; }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/messages/bulk`, null, { params: { subject: bulkMessage.subject, content: bulkMessage.content, recipient_ids: selectedStudentsForBulk }, withCredentials: true, headers });
      toast.success(`${selectedStudentsForBulk.length} mesaj gönderildi`);
      setShowBulkMessageModal(false);
      setSelectedStudentsForBulk([]);
      setBulkMessage({ subject: '', content: '' });
    } catch (error) { toast.error('Toplu mesaj gönderilemedi'); }
  };

  // Export
  const handleExportStudents = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/export/students`, { withCredentials: true, headers });
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `students_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success('Dışa aktarıldı');
    } catch (error) { toast.error('Dışa aktarılamadı'); }
  };

  // Content operations
  const openContentModal = (contentType) => {
    setSelectedContentType(contentType);
    setShowContentModal(true);
    loadQuestions(contentType.category);
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.question_text || !newQuestion.correct_answer) { toast.error('Soru ve cevap zorunludur'); return; }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/questions`, newQuestion, { withCredentials: true, headers });
      toast.success('Soru eklendi');
      setShowAddQuestionModal(false);
      setNewQuestion({ question_text: '', question_type: 'multiple_choice', options: ['', '', '', ''], correct_answer: '', difficulty: 'medium', category: 'general', module: 'comprehensive' });
      if (selectedContentType) loadQuestions(selectedContentType.category);
    } catch (error) { toast.error('Soru eklenemedi'); }
  };

  const handleOpenEditModal = (question) => {
    setEditingQuestion({
      ...question,
      options: question.options || ['', '', '', '']
    });
    setShowEditQuestionModal(true);
  };

  const handleEditQuestion = async () => {
    if (!editingQuestion || !editingQuestion.question_text || !editingQuestion.correct_answer) { 
      toast.error('Soru ve cevap zorunludur'); 
      return; 
    }
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/questions/${editingQuestion.id}`, editingQuestion, { withCredentials: true, headers });
      toast.success('Soru güncellendi');
      setShowEditQuestionModal(false);
      setEditingQuestion(null);
      if (selectedContentType) loadQuestions(selectedContentType.category);
    } catch (error) { toast.error('Güncellenemedi'); }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Bu soruyu silmek istediğinize emin misiniz?')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/questions/${questionId}`, { withCredentials: true, headers });
      toast.success('Soru silindi');
      if (selectedContentType) loadQuestions(selectedContentType.category);
    } catch (error) { toast.error('Silinemedi'); }
  };

  const filteredUsers = users.filter(u => (u.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || (u.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()));
  const formatDate = (dateStr) => { if (!dateStr) return '-'; const date = new Date(dateStr); return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };
  const getLevelBadge = (xp) => { if (miles >= 2500) return { label: 'Master', color: 'bg-amber-500' }; if (miles >= 1000) return { label: 'İleri', color: 'bg-purple-500' }; if (miles >= 500) return { label: 'Orta', color: 'bg-blue-500' }; return { label: 'Başlangıç', color: 'bg-green-500' }; };
  
  const studentCount = users.filter(u => u.role === 'student').length;
  const activeStudents = users.filter(u => u.role === 'student' && u.status !== 'inactive').length;
  const totalMiles = users.reduce((sum, u) => sum + (u.miles || 0), 0);

  // MongoDB'deki içerikler - Kabin Ekibi içerikleri (ICAO, Anonslar, Mülakat, Acil Durum) 
  // aşağıdaki "Kabin Ekibi Eğitim Modülleri" bölümünde statik olarak gösteriliyor
  const contentTypes = [
    { id: 'grammar', title: 'Gramer Eğitimi', category: 'grammar', count: 139, icon: GraduationCap, color: 'from-blue-500 to-cyan-500' },
    { id: 'vocabulary', title: 'Kelime Bilgisi', category: 'vocabulary', count: 69, icon: BookOpen, color: 'from-amber-500 to-orange-500' },
    { id: 'reading', title: 'Okuma Anlama', category: 'reading', count: 30, icon: Languages, color: 'from-green-500 to-emerald-500' },
    { id: 'sentence', title: 'Cümle Tamamlama', category: 'sentence completion', count: 2, icon: MessageSquare, color: 'from-purple-500 to-pink-500' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <div className="w-20 h-20 border-8 border-amber-200 border-t-amber-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-xl font-bold text-white">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Registration Requests
  const renderRegistrations = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h2 className="text-3xl font-black text-gray-800">Kayıt Talepleri</h2><p className="text-gray-600">Bekleyen kayıt başvurularını yönetin</p></div>
        <Button onClick={loadRegistrationRequests} variant="outline" className="gap-2"><RefreshCw className="w-4 h-4" /> Yenile</Button>
      </div>
      
      <Card>
        <CardContent className="p-0">
          {registrationRequests.filter(r => r.status === 'pending').length === 0 ? (
            <div className="p-8 text-center text-gray-500">Bekleyen kayıt talebi yok</div>
          ) : (
            <div className="divide-y">
              {registrationRequests.filter(r => r.status === 'pending').map(req => (
                <div key={req.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-600">
                      {req.first_name?.[0]}{req.last_name?.[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{req.name}</p>
                      <p className="text-sm text-gray-500">{req.email}</p>
                      <p className="text-xs text-gray-400">Tel: {req.phone} | Danışman: {req.counselor_name || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input placeholder="Şifre" value={approvePassword} onChange={e => setApprovePassword(e.target.value)} className="w-32" />
                    <Button onClick={() => handleApproveRegistration(req.id, true)} variant="outline" className="text-yellow-600 border-yellow-300">Demo Olarak Onayla</Button>
                    <Button onClick={() => handleApproveRegistration(req.id, false)} className="bg-green-600 hover:bg-green-700">Onayla</Button>
                    <Button onClick={() => handleRejectRegistration(req.id)} variant="outline" className="text-red-600 border-red-300">Reddet</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Geçmiş Talepler */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Geçmiş Talepler</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {registrationRequests.filter(r => r.status !== 'pending').map(req => (
              <div key={req.id} className="p-4 flex items-center justify-between opacity-60">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-500">{req.first_name?.[0]}{req.last_name?.[0]}</div>
                  <div>
                    <p className="font-medium text-gray-700">{req.name} - {req.email}</p>
                  </div>
                </div>
                <Badge className={req.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                  {req.status === 'approved' ? '✓ Onaylandı' : '✗ Reddedildi'}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Demo Management
  const renderDemoManagement = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h2 className="text-3xl font-black text-gray-800">Demo Öğrenci Yönetimi</h2><p className="text-gray-600">8 saatlik demo hesapları oluşturun ve yönetin</p></div>
        <Button onClick={() => setShowDemoModal(true)} className="bg-yellow-500 hover:bg-yellow-600 gap-2"><UserPlus className="w-4 h-4" /> Yeni Demo Öğrenci</Button>
      </div>
      
      <Card>
        <CardHeader><CardTitle className="text-lg">Aktif Demo Kullanıcılar</CardTitle></CardHeader>
        <CardContent className="p-0">
          {demoUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Aktif demo kullanıcı yok</div>
          ) : (
            <div className="divide-y">
              {demoUsers.map(demoUser => {
                const expiresAt = demoUser.demo_expires_at ? new Date(demoUser.demo_expires_at) : null;
                const isExpired = expiresAt && expiresAt < new Date();
                const hoursLeft = expiresAt ? Math.max(0, Math.round((expiresAt - new Date()) / (1000 * 60 * 60))) : 0;
                
                return (
                  <div key={demoUser.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${isExpired ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-600'}`}>
                        {demoUser.first_name?.[0]}{demoUser.last_name?.[0]}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{demoUser.name}</p>
                        <p className="text-sm text-gray-500">{demoUser.email}</p>
                        <p className="text-xs text-gray-400">Oluşturulma: {new Date(demoUser.created_at).toLocaleDateString('tr-TR')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {isExpired ? (
                        <Badge className="bg-red-100 text-red-700">Süresi Doldu</Badge>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-700">{hoursLeft} saat kaldı</Badge>
                      )}
                      <Button onClick={() => handleExtendDemo(demoUser.id, 8)} variant="outline" size="sm">+8 Saat</Button>
                      <Button onClick={() => handleConvertToFull(demoUser.id)} className="bg-green-600 hover:bg-green-700" size="sm">Tam Sürüme Geçir</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Eğitime Katılma Talepleri */}
      {demoInterests.filter(i => i.status === 'pending').length > 0 && (
        <Card className="border-2 border-green-300 bg-green-50">
          <CardHeader><CardTitle className="text-lg text-green-800">📩 Eğitime Katılma Talepleri</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {demoInterests.filter(i => i.status === 'pending').map(interest => (
                <div key={interest.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-200 flex items-center justify-center font-bold text-green-700">
                      {interest.user_name?.[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{interest.user_name}</p>
                      <p className="text-sm text-gray-500">{interest.user_email}</p>
                      <p className="text-xs text-green-600 mt-1">"{interest.message}"</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700">Demo Süresi Dolmuş</Badge>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={async () => {
                      const headers = getAuthHeaders();
                      await axios.post(`${API}/admin/demo-interests/${interest.id}/update-status`, { status: 'contacted' }, { withCredentials: true, headers });
                      toast.success('İletişime geçildi olarak işaretlendi');
                      loadDemoUsers();
                    }}>İletişime Geçildi</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Demo Oluşturma Modal */}
      <Dialog open={showDemoModal} onOpenChange={setShowDemoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yeni Demo Öğrenci Oluştur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Ad</Label><Input value={newDemoUser.first_name} onChange={e => setNewDemoUser({...newDemoUser, first_name: e.target.value})} placeholder="Ad" /></div>
              <div><Label>Soyad</Label><Input value={newDemoUser.last_name} onChange={e => setNewDemoUser({...newDemoUser, last_name: e.target.value})} placeholder="Soyad" /></div>
            </div>
            <div><Label>E-posta</Label><Input value={newDemoUser.email} onChange={e => setNewDemoUser({...newDemoUser, email: e.target.value})} placeholder="demo@ornek.com" /></div>
            <div><Label>Şifre</Label><Input value={newDemoUser.password} onChange={e => setNewDemoUser({...newDemoUser, password: e.target.value})} placeholder="demo123" /></div>
            <div className="bg-yellow-50 p-3 rounded-lg text-sm text-yellow-800">
              ⏰ Demo hesabı 8 saat geçerli olacak. Süre dolduğunda kullanıcı sisteme giremez.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDemoModal(false)}>İptal</Button>
            <Button onClick={handleCreateDemoUser} className="bg-yellow-500 hover:bg-yellow-600">Demo Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Dashboard
  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h2 className="text-3xl font-black text-gray-800">Dashboard</h2><p className="text-gray-600">Hoş geldiniz, {user?.name || 'Admin'}! 👋</p></div>
        <Button onClick={loadAdminData} variant="outline" className="gap-2"><RefreshCw className="w-4 h-4" /> Yenile</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard title="Toplam Öğrenci" value={studentCount} icon={Users} color="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100" trend="up" trendValue="+12%" />
        <StatsCard title="Aktif Öğrenci" value={activeStudents} icon={Activity} color="border-green-200 bg-gradient-to-br from-green-50 to-green-100" />
        <StatsCard title="Toplam Mil" value={totalMiles.toLocaleString()} icon={Star} color="border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100" />
        <StatsCard title="Kurs Sayısı" value={courses.length} icon={BookOpen} color="border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-green-500" /> En Başarılı</CardTitle></CardHeader>
          <CardContent>
            {users.filter(u => u.role === 'student').sort((a, b) => (b.miles || 0) - (a.miles || 0)).slice(0, 5).map((s, i) => (
              <div key={s.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-2">
                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-700' : 'bg-gray-300'}`}>{i + 1}</span>
                <div className="flex-1"><p className="font-medium">{s.name || 'İsimsiz'}</p><p className="text-xs text-gray-500">{s.email}</p></div>
                <Badge className="bg-amber-100 text-amber-700">{s.miles || 0} Mil</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Activity className="w-5 h-5 text-blue-500" /> Son Kayıtlar</CardTitle></CardHeader>
          <CardContent>
            {users.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 5).map((s) => (
              <div key={s.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">{(s.name || s.email || '?')[0].toUpperCase()}</div>
                <div className="flex-1"><p className="font-medium">{s.name || 'İsimsiz'}</p><p className="text-xs text-gray-500">{formatDate(s.created_at)}</p></div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Students
  const renderStudents = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div><h2 className="text-3xl font-black text-gray-800">Öğrenci Yönetimi</h2><p className="text-gray-600">{studentCount} öğrenci</p></div>
        <div className="flex items-center gap-3">
          <div className="relative"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" /><Input placeholder="Ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 w-64" /></div>
          <Button onClick={() => setShowAddStudentModal(true)} className="gap-2 bg-green-500 hover:bg-green-600"><UserPlus className="w-4 h-4" /> Yeni</Button>
          <Button onClick={() => setShowBulkMessageModal(true)} variant="outline" className="gap-2"><Send className="w-4 h-4" /> Toplu Mesaj</Button>
          <Button onClick={handleExportStudents} variant="outline" className="gap-2"><Download className="w-4 h-4" /> Dışa Aktar</Button>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200"><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-blue-600">{studentCount}</p><p className="text-sm">Toplam</p></CardContent></Card>
        <Card className="bg-green-50 border-green-200"><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-green-600">{activeStudents}</p><p className="text-sm">Aktif</p></CardContent></Card>
        <Card className="bg-amber-50 border-amber-200"><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-amber-600">{users.filter(u => u.profile_completed).length}</p><p className="text-sm">Profil Tam</p></CardContent></Card>
        <Card className="bg-purple-50 border-purple-200"><CardContent className="p-4 text-center"><p className="text-3xl font-bold text-purple-600">{users.filter(u => (u.miles || 0) >= 1000).length}</p><p className="text-sm">İleri</p></CardContent></Card>
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b"><tr><th className="text-left p-4">Öğrenci</th><th className="text-left p-4">Mil</th><th className="text-left p-4">Seri</th><th className="text-left p-4">Kayıt</th><th className="text-left p-4">Durum</th><th className="text-left p-4">İşlemler</th></tr></thead>
            <tbody>
              {filteredUsers.filter(u => u.role !== 'admin').map((s) => {
                const level = getLevelBadge(s.miles || 0);
                return (
                  <tr key={s.id} className="border-b hover:bg-gray-50">
                    <td className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">{(s.name || s.email || '?')[0].toUpperCase()}</div><div><p className="font-medium">{s.name || 'İsimsiz'}</p><p className="text-sm text-gray-500">{s.email}</p></div></div></td>
                    <td className="p-4"><div className="flex items-center gap-2"><Star className="w-4 h-4 text-amber-500" />{s.miles || 0}</div><Badge className={`mt-1 ${level.color} text-white text-xs`}>{level.label}</Badge></td>
                    <td className="p-4"><div className="flex items-center gap-2"><Flame className="w-4 h-4 text-orange-500" />{s.current_streak || 0}</div></td>
                    <td className="p-4 text-sm">{formatDate(s.created_at)}</td>
                    <td className="p-4"><Badge className={s.status === 'inactive' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>{s.status === 'inactive' ? 'Pasif' : 'Aktif'}</Badge></td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => handleViewStudent(s)} title="Detay"><Eye className="w-4 h-4" /></Button>
                        <Button size="sm" variant="outline" onClick={() => { setSelectedStudent(s); setShowAssignCourseModal(true); }} title="Ders Ata"><BookOpen className="w-4 h-4" /></Button>
                        <Button size="sm" variant="outline" onClick={() => { setNewMessage({ ...newMessage, recipient_id: s.id }); setShowMessageModal(true); }} title="Mesaj"><Mail className="w-4 h-4" /></Button>
                        <Button size="sm" variant="outline" onClick={() => handleToggleStatus(s.id)} title="Durum">{s.status === 'inactive' ? <ToggleLeft className="w-4 h-4" /> : <ToggleRight className="w-4 h-4" />}</Button>
                        <Button size="sm" variant="outline" onClick={() => handleResetPassword(s.id)} title="Şifre"><Key className="w-4 h-4" /></Button>
                        <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteStudent(s.id)} title="Sil"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );

  // LMS
  const renderLMS = () => (
    <div className="space-y-6">
      <div><h2 className="text-3xl font-black text-gray-800">LMS Sistemi</h2><p className="text-gray-600">Ders ve kurs yönetimi</p></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard title="Toplam Kurs" value={courses.length} icon={BookOpen} color="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100" />
        <StatsCard title="Atanan" value={lmsStats?.total_assignments || 0} icon={ClipboardList} color="border-green-200 bg-gradient-to-br from-green-50 to-green-100" />
        <StatsCard title="Tamamlanan" value={lmsStats?.completed_assignments || 0} icon={CheckCircle} color="border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100" />
        <StatsCard title="İlerleyen" value={lmsStats?.students_with_progress || 0} icon={TrendingUp} color="border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100" />
      </div>
      <Card><CardHeader><CardTitle>Mevcut Kurslar</CardTitle></CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((c) => (
              <Card key={c.id} className="hover:shadow-lg transition-all">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${c.category === 'core' ? 'bg-blue-500' : c.category === 'vocabulary' ? 'bg-amber-500' : c.category === 'speaking' ? 'bg-pink-500' : c.category === 'safety' ? 'bg-red-500' : 'bg-purple-500'}`}><BookOpen className="w-6 h-6 text-white" /></div>
                    <div><h4 className="font-bold">{c.name}</h4><p className="text-xs text-gray-500">{c.category}</p></div>
                  </div>
                  <Badge variant="outline">+{c.miles_reward} Mil</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Content
  const renderContent = () => (
    <ContentManager />
  );

  // Announcements
  const renderAnnouncements = () => {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div><h2 className="text-3xl font-black text-gray-800">Duyuru Yönetimi</h2><p className="text-gray-600">Öğrencilere duyuru gönderin</p></div>
          <Button onClick={() => setShowAnnouncementModal(true)} className="gap-2 bg-amber-500 hover:bg-amber-600"><Megaphone className="w-4 h-4" /> Yeni Duyuru</Button>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {announcements.map((a) => (
            <Card key={a.id} className={`border-l-4 ${a.priority === 'urgent' ? 'border-l-red-500' : a.priority === 'high' ? 'border-l-orange-500' : 'border-l-blue-500'}`}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold">{a.title}</h3>
                  <Badge className={a.priority === 'urgent' ? 'bg-red-500' : a.priority === 'high' ? 'bg-orange-500' : 'bg-blue-500'}>{a.priority}</Badge>
                </div>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">{a.content}</p>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">{formatDate(a.created_at)}</span>
                  <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteAnnouncement(a.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {announcements.length === 0 && <div className="col-span-full text-center py-12 text-gray-500"><Megaphone className="w-16 h-16 mx-auto mb-4 text-gray-300" /><p>Henüz duyuru yok</p></div>}
        </div>
      </div>
    );
  };

  // Messages
  const renderMessages = () => {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div><h2 className="text-3xl font-black text-gray-800">Mesajlar</h2><p className="text-gray-600">Öğrencilerle iletişim</p></div>
          <div className="flex gap-2">
            <Button onClick={() => setShowMessageModal(true)} className="gap-2 bg-pink-500 hover:bg-pink-600"><MessageCircle className="w-4 h-4" /> Yeni Mesaj</Button>
            <Button onClick={() => setShowBulkMessageModal(true)} variant="outline" className="gap-2"><Users2 className="w-4 h-4" /> Toplu</Button>
          </div>
        </div>
        <Card>
          <CardContent className="p-0 divide-y max-h-[600px] overflow-y-auto">
            {messages.map((m) => (
              <div key={m.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center text-white"><Mail className="w-5 h-5" /></div>
                  <div className="flex-1">
                    <div className="flex justify-between"><h4 className="font-bold">{m.subject}</h4><Badge variant="outline" className="text-xs">{m.is_read ? 'Okundu' : 'Gönderildi'}</Badge></div>
                    <p className="text-sm text-gray-500">Alıcı: {m.recipient_name}</p>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{m.content}</p>
                    <p className="text-xs text-gray-400 mt-2">{formatDate(m.created_at)}</p>
                  </div>
                </div>
              </div>
            ))}
            {messages.length === 0 && <div className="p-12 text-center text-gray-500"><MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" /><p>Henüz mesaj yok</p></div>}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Reports
  const renderReports = () => {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div><h2 className="text-3xl font-black text-gray-800">Raporlar & Analizler</h2><p className="text-gray-600">Detaylı istatistikler</p></div>
          <div className="flex gap-2">
            <Button onClick={loadReports} variant="outline" className="gap-2"><RefreshCw className="w-4 h-4" /> Yenile</Button>
            <Button variant="outline" className="gap-2"><Download className="w-4 h-4" /> Rapor İndir</Button>
          </div>
        </div>
        {reportsData && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatsCard title="Toplam Öğrenci" value={reportsData.students?.total || 0} icon={Users} color="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100" />
              <StatsCard title="Toplam Mil" value={(reportsData.miles?.total || 0).toLocaleString()} icon={Star} color="border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100" />
              <StatsCard title="Ortalama Mil" value={Math.round(reportsData.miles?.average || 0)} icon={BarChart2} color="border-green-200 bg-gradient-to-br from-green-50 to-green-100" />
              <StatsCard title="En Yüksek Seri" value={reportsData.streaks?.highest || 0} icon={Flame} color="border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100" />
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle>Seviye Dağılımı</CardTitle></CardHeader>
                <CardContent>
                  {Object.entries(reportsData.level_distribution || {}).map(([level, count]) => (
                    <div key={level} className="flex items-center gap-3 mb-3">
                      <div className={`w-4 h-4 rounded ${level === 'master' ? 'bg-amber-500' : level === 'advanced' ? 'bg-purple-500' : level === 'intermediate' ? 'bg-blue-500' : 'bg-green-500'}`}></div>
                      <span className="flex-1 capitalize">{level === 'beginner' ? 'Başlangıç' : level === 'intermediate' ? 'Orta' : level === 'advanced' ? 'İleri' : 'Master'}</span>
                      <span className="font-bold">{count}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Haftalık Kayıtlar</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-end justify-between gap-2 h-32">
                    {(reportsData.weekly_registrations || []).map((d, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div className="w-full bg-gradient-to-t from-blue-500 to-cyan-400 rounded-t" style={{ height: `${Math.max((d.count || 0) * 20, 4)}px` }}></div>
                        <span className="text-xs text-gray-500 mt-2">{d.day}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
        {examReports && (
          <Card>
            <CardHeader><CardTitle>Sınav Sonuçları</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-blue-50 rounded-xl"><p className="text-3xl font-bold text-blue-600">{examReports.total_exams}</p><p className="text-sm">Toplam Sınav</p></div>
                <div className="text-center p-4 bg-green-50 rounded-xl"><p className="text-3xl font-bold text-green-600">{examReports.average_score}%</p><p className="text-sm">Ortalama</p></div>
                <div className="text-center p-4 bg-purple-50 rounded-xl"><p className="text-3xl font-bold text-purple-600">{examReports.pass_rate}%</p><p className="text-sm">Geçme Oranı</p></div>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {examReports.recent_exams?.slice(0, 10).map((e, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div><p className="font-medium">{e.user_name || 'Bilinmiyor'}</p><p className="text-xs text-gray-500">{e.determined_level}</p></div>
                    <Badge className={e.score >= 60 ? 'bg-green-500' : 'bg-red-500'}>{e.score}%</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // Logs
  const renderLogs = () => {
    const actionLabels = { 'student_registered': '🎓 Kayıt', 'courses_assigned': '📚 Ders Atama', 'password_reset': '🔑 Şifre', 'student_status_changed': '🔄 Durum', 'announcement_created': '📢 Duyuru', 'message_sent': '✉️ Mesaj', 'data_exported': '📤 Export' };
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div><h2 className="text-3xl font-black text-gray-800">Aktivite Logları</h2><p className="text-gray-600">Sistem kayıtları</p></div>
          <Button onClick={loadActivityLogs} variant="outline" className="gap-2"><RefreshCw className="w-4 h-4" /> Yenile</Button>
        </div>
        <Card>
          <CardContent className="p-0 divide-y max-h-[600px] overflow-y-auto">
            {activityLogs.map((log, i) => (
              <div key={i} className="p-4 hover:bg-gray-50 flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center"><Activity className="w-5 h-5 text-blue-600" /></div>
                <div className="flex-1">
                  <div className="flex items-center gap-2"><Badge variant="outline">{actionLabels[log.action] || log.action}</Badge><span className="text-sm text-gray-500">by {log.user_name || 'System'}</span></div>
                  <p className="text-sm text-gray-600 mt-1">{JSON.stringify(log.details).slice(0, 80)}...</p>
                  <p className="text-xs text-gray-400 mt-1">{formatDate(log.timestamp)}</p>
                </div>
              </div>
            ))}
            {activityLogs.length === 0 && <div className="p-12 text-center text-gray-500"><History className="w-16 h-16 mx-auto mb-4 text-gray-300" /><p>Log yok</p><Button onClick={loadActivityLogs} variant="outline" className="mt-4">Yükle</Button></div>}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Settings
  const renderSettings = () => (
    <div className="space-y-6">
      <h2 className="text-3xl font-black text-gray-800">Ayarlar</h2>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card><CardHeader><CardTitle><Settings className="w-5 h-5 inline mr-2" />Genel</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><label className="text-sm font-medium">Site Adı</label><Input defaultValue="AirLingo" className="mt-1" /></div>
            <div><label className="text-sm font-medium">Admin Email</label><Input defaultValue="admin@example.com" className="mt-1" /></div>
            <Button className="w-full gap-2"><Save className="w-4 h-4" /> Kaydet</Button>
          </CardContent>
        </Card>
        <Card><CardHeader><CardTitle><Database className="w-5 h-5 inline mr-2 text-blue-500" />Veritabanı</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-green-50 rounded-lg flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500" /><span className="font-medium text-green-700">MongoDB Bağlı</span></div>
            <div className="flex gap-2"><Button variant="outline" className="flex-1 gap-2"><Download className="w-4 h-4" /> Yedekle</Button><Button variant="outline" className="flex-1 gap-2"><Upload className="w-4 h-4" /> Geri Yükle</Button></div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50">
      <AdminSidebar activeTab={activeTab} setActiveTab={setActiveTab} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} onLogout={handleLogout} />
      <button onClick={() => setSidebarOpen(true)} className={`lg:hidden fixed top-4 left-4 z-30 p-3 bg-gradient-to-r from-amber-600 to-red-600 rounded-xl shadow-lg text-white ${sidebarOpen ? 'hidden' : 'flex'}`}><Menu className="w-5 h-5" /></button>
      <main className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'} p-4 lg:p-8`}>
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'registrations' && renderRegistrations()}
        {activeTab === 'students' && renderStudents()}
        {activeTab === 'demo' && renderDemoManagement()}
        {activeTab === 'lms' && renderLMS()}
        {activeTab === 'content' && renderContent()}
        {activeTab === 'announcements' && renderAnnouncements()}
        {activeTab === 'messages' && renderMessages()}
        {activeTab === 'reports' && renderReports()}
        {activeTab === 'logs' && renderLogs()}
        {activeTab === 'settings' && renderSettings()}
      </main>

      {/* Add Student Modal */}
      {showAddStudentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md"><CardHeader><CardTitle><UserPlus className="w-5 h-5 inline mr-2 text-green-500" />Yeni Öğrenci</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><label className="text-sm font-medium">Ad Soyad *</label><Input value={newStudent.name} onChange={(e) => setNewStudent({...newStudent, name: e.target.value})} className="mt-1" /></div>
              <div><label className="text-sm font-medium">E-posta *</label><Input type="email" value={newStudent.email} onChange={(e) => setNewStudent({...newStudent, email: e.target.value})} className="mt-1" /></div>
              <div><label className="text-sm font-medium">Telefon</label><Input value={newStudent.phone} onChange={(e) => setNewStudent({...newStudent, phone: e.target.value})} className="mt-1" /></div>
              <div><label className="text-sm font-medium">Şifre</label><Input value={newStudent.password} onChange={(e) => setNewStudent({...newStudent, password: e.target.value})} className="mt-1" /><p className="text-xs text-gray-500 mt-1">Varsayılan: student123</p></div>
              <div className="flex gap-3 pt-4"><Button variant="outline" className="flex-1" onClick={() => setShowAddStudentModal(false)}>İptal</Button><Button className="flex-1 bg-green-500" onClick={handleAddStudent}>Ekle</Button></div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Student Detail Modal */}
      {showStudentDetailModal && studentDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between"><CardTitle>Öğrenci Detayı</CardTitle><Button variant="ghost" size="sm" onClick={() => setShowStudentDetailModal(false)}><X className="w-4 h-4" /></Button></CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">{(studentDetails.student?.name || '?')[0].toUpperCase()}</div>
                <div><h3 className="text-xl font-bold">{studentDetails.student?.name}</h3><p className="text-gray-500">{studentDetails.student?.email}</p>
                  <div className="flex gap-2 mt-2"><Badge className="bg-amber-100 text-amber-700">{studentDetails.student?.miles || 0} Mil</Badge><Badge className="bg-orange-100 text-orange-700">{studentDetails.student?.current_streak || 0} Gün</Badge></div>
                </div>
              </div>
              {studentDetails.activity_logs?.length > 0 && (
                <div><h4 className="font-bold mb-2">Son Aktiviteler</h4><div className="space-y-2 max-h-40 overflow-y-auto">{studentDetails.activity_logs.slice(0, 5).map((log, i) => (<div key={i} className="text-sm p-2 bg-gray-50 rounded flex justify-between"><span>{log.action}</span><span className="text-gray-500">{formatDate(log.timestamp)}</span></div>))}</div></div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Assign Course Modal */}
      {showAssignCourseModal && selectedStudent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg"><CardHeader><CardTitle>Ders Ata: {selectedStudent.name}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">{courses.map((c) => (<label key={c.id} className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer border-2 ${selectedCourses.includes(c.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}><input type="checkbox" checked={selectedCourses.includes(c.id)} onChange={(e) => { if (e.target.checked) setSelectedCourses([...selectedCourses, c.id]); else setSelectedCourses(selectedCourses.filter(id => id !== c.id)); }} className="w-4 h-4" /><span className="text-sm">{c.name}</span></label>))}</div>
              <div className="flex gap-3 pt-4"><Button variant="outline" className="flex-1" onClick={() => { setShowAssignCourseModal(false); setSelectedCourses([]); }}>İptal</Button><Button className="flex-1 bg-blue-500" onClick={handleAssignCourses} disabled={selectedCourses.length === 0}>{selectedCourses.length} Ders Ata</Button></div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Announcement Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg"><CardHeader><CardTitle><Megaphone className="w-5 h-5 inline mr-2 text-amber-500" />Yeni Duyuru</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><label className="text-sm font-medium">Başlık *</label><Input value={newAnnouncement.title} onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})} className="mt-1" /></div>
              <div><label className="text-sm font-medium">İçerik *</label><textarea value={newAnnouncement.content} onChange={(e) => setNewAnnouncement({...newAnnouncement, content: e.target.value})} className="w-full mt-1 p-3 border rounded-lg min-h-[100px]" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm font-medium">Öncelik</label><select value={newAnnouncement.priority} onChange={(e) => setNewAnnouncement({...newAnnouncement, priority: e.target.value})} className="w-full mt-1 p-2 border rounded-lg"><option value="low">Düşük</option><option value="normal">Normal</option><option value="high">Yüksek</option><option value="urgent">Acil</option></select></div>
                <div><label className="text-sm font-medium">Hedef</label><select value={newAnnouncement.target_audience} onChange={(e) => setNewAnnouncement({...newAnnouncement, target_audience: e.target.value})} className="w-full mt-1 p-2 border rounded-lg"><option value="all">Herkes</option><option value="students">Öğrenciler</option></select></div>
              </div>
              <div className="flex gap-3 pt-4"><Button variant="outline" className="flex-1" onClick={() => setShowAnnouncementModal(false)}>İptal</Button><Button className="flex-1 bg-amber-500" onClick={handleCreateAnnouncement}>Yayınla</Button></div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg"><CardHeader><CardTitle><MessageCircle className="w-5 h-5 inline mr-2 text-pink-500" />Mesaj Gönder</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><label className="text-sm font-medium">Alıcı *</label><select value={newMessage.recipient_id} onChange={(e) => setNewMessage({...newMessage, recipient_id: e.target.value})} className="w-full mt-1 p-2 border rounded-lg"><option value="">Seçin...</option>{users.filter(u => u.role === 'student').map(u => (<option key={u.id} value={u.id}>{u.name || u.email}</option>))}</select></div>
              <div><label className="text-sm font-medium">Konu *</label><Input value={newMessage.subject} onChange={(e) => setNewMessage({...newMessage, subject: e.target.value})} className="mt-1" /></div>
              <div><label className="text-sm font-medium">Mesaj *</label><textarea value={newMessage.content} onChange={(e) => setNewMessage({...newMessage, content: e.target.value})} className="w-full mt-1 p-3 border rounded-lg min-h-[100px]" /></div>
              <div className="flex gap-3 pt-4"><Button variant="outline" className="flex-1" onClick={() => setShowMessageModal(false)}>İptal</Button><Button className="flex-1 bg-pink-500" onClick={handleSendMessage}>Gönder</Button></div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Message Modal */}
      {showBulkMessageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl"><CardHeader><CardTitle><Users2 className="w-5 h-5 inline mr-2 text-purple-500" />Toplu Mesaj</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><label className="text-sm font-medium">Alıcılar ({selectedStudentsForBulk.length} seçili)</label>
                <div className="grid grid-cols-3 gap-2 mt-2 max-h-40 overflow-y-auto">{users.filter(u => u.role === 'student').map(u => (<label key={u.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm ${selectedStudentsForBulk.includes(u.id) ? 'bg-purple-100' : 'bg-gray-50'}`}><input type="checkbox" checked={selectedStudentsForBulk.includes(u.id)} onChange={(e) => { if (e.target.checked) setSelectedStudentsForBulk([...selectedStudentsForBulk, u.id]); else setSelectedStudentsForBulk(selectedStudentsForBulk.filter(id => id !== u.id)); }} className="w-4 h-4" />{u.name || u.email}</label>))}</div>
                <div className="mt-2 flex gap-2"><Button size="sm" variant="outline" onClick={() => setSelectedStudentsForBulk(users.filter(u => u.role === 'student').map(u => u.id))}>Hepsini Seç</Button><Button size="sm" variant="outline" onClick={() => setSelectedStudentsForBulk([])}>Temizle</Button></div>
              </div>
              <div><label className="text-sm font-medium">Konu *</label><Input value={bulkMessage.subject} onChange={(e) => setBulkMessage({...bulkMessage, subject: e.target.value})} className="mt-1" /></div>
              <div><label className="text-sm font-medium">Mesaj *</label><textarea value={bulkMessage.content} onChange={(e) => setBulkMessage({...bulkMessage, content: e.target.value})} className="w-full mt-1 p-3 border rounded-lg min-h-[100px]" /></div>
              <div className="flex gap-3 pt-4"><Button variant="outline" className="flex-1" onClick={() => setShowBulkMessageModal(false)}>İptal</Button><Button className="flex-1 bg-purple-500" onClick={handleSendBulkMessage} disabled={selectedStudentsForBulk.length === 0}>{selectedStudentsForBulk.length} Kişiye Gönder</Button></div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Content List Modal */}
      {showContentModal && selectedContentType && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between border-b">
              <div className="flex items-center gap-3"><div className={`w-10 h-10 bg-gradient-to-br ${selectedContentType.color} rounded-lg flex items-center justify-center`}><selectedContentType.icon className="w-5 h-5 text-white" /></div><div><CardTitle>{selectedContentType.title}</CardTitle><p className="text-sm text-gray-500">{questions.length} içerik</p></div></div>
              <div className="flex gap-2"><Button size="sm" onClick={() => { setNewQuestion({...newQuestion, category: selectedContentType.category}); setShowAddQuestionModal(true); }} className="bg-green-500"><Plus className="w-4 h-4 mr-1" /> Ekle</Button><Button variant="ghost" size="sm" onClick={() => setShowContentModal(false)}><X className="w-4 h-4" /></Button></div>
            </CardHeader>
            <CardContent className="p-0 overflow-y-auto flex-1">
              {questions.length === 0 ? (<div className="p-8 text-center text-gray-500"><BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" /><p>Henüz içerik yok</p></div>) : (
                <div className="divide-y">{questions.map((q, i) => (
                  <div key={q.id || i} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-bold">{i + 1}</span>
                      <div className="flex-1 min-w-0"><p className="font-medium break-words">{q.question_text || q.text || 'İçerik'}</p><div className="flex flex-wrap gap-2 mt-2"><Badge variant="outline" className="text-xs">{q.difficulty === 'easy' ? '🟢 Kolay' : q.difficulty === 'hard' ? '🔴 Zor' : '🟡 Orta'}</Badge>{q.correct_answer && <Badge className="bg-green-100 text-green-700 text-xs">Cevap: {q.correct_answer}</Badge>}</div></div>
                      <div className="flex gap-1"><Button size="sm" variant="outline" className="text-blue-500" onClick={() => handleOpenEditModal(q)}><Edit className="w-4 h-4" /></Button><Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteQuestion(q.id)}><Trash2 className="w-4 h-4" /></Button></div>
                    </div>
                  </div>
                ))}</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Question Modal */}
      {showAddQuestionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl"><CardHeader><CardTitle><Plus className="w-5 h-5 inline mr-2 text-green-500" />Yeni Soru</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><label className="text-sm font-medium">Soru *</label><textarea value={newQuestion.question_text} onChange={(e) => setNewQuestion({...newQuestion, question_text: e.target.value})} className="w-full mt-1 p-3 border rounded-lg min-h-[100px]" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm font-medium">Tip</label><select value={newQuestion.question_type} onChange={(e) => setNewQuestion({...newQuestion, question_type: e.target.value})} className="w-full mt-1 p-2 border rounded-lg"><option value="multiple_choice">Çoktan Seçmeli</option><option value="true_false">Doğru/Yanlış</option><option value="fill_blank">Boşluk Doldurma</option></select></div>
                <div><label className="text-sm font-medium">Zorluk</label><select value={newQuestion.difficulty} onChange={(e) => setNewQuestion({...newQuestion, difficulty: e.target.value})} className="w-full mt-1 p-2 border rounded-lg"><option value="easy">Kolay</option><option value="medium">Orta</option><option value="hard">Zor</option></select></div>
              </div>
              <div><label className="text-sm font-medium">Şıklar</label><div className="space-y-2 mt-1">{newQuestion.options.map((opt, i) => (<Input key={i} value={opt} onChange={(e) => { const newOpts = [...newQuestion.options]; newOpts[i] = e.target.value; setNewQuestion({...newQuestion, options: newOpts}); }} placeholder={`Şık ${String.fromCharCode(65 + i)}`} />))}</div></div>
              <div><label className="text-sm font-medium">Doğru Cevap *</label><Input value={newQuestion.correct_answer} onChange={(e) => setNewQuestion({...newQuestion, correct_answer: e.target.value})} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm font-medium">Kategori</label><select value={newQuestion.category} onChange={(e) => setNewQuestion({...newQuestion, category: e.target.value})} className="w-full mt-1 p-2 border rounded-lg"><option value="grammar">Gramer</option><option value="vocabulary">Kelime Bilgisi</option><option value="reading">Okuma Anlama</option><option value="sentence completion">Cümle Tamamlama</option><option value="interview">Mülakat</option><option value="emergency">Acil Durum</option><option value="icao">ICAO</option><option value="announcements">Anonslar</option></select></div>
                <div><label className="text-sm font-medium">Modül</label><select value={newQuestion.module} onChange={(e) => setNewQuestion({...newQuestion, module: e.target.value})} className="w-full mt-1 p-2 border rounded-lg"><option value="comprehensive">64 Soruluk</option><option value="flashcards">Flashcard</option><option value="interview">Mülakat</option><option value="emergency">Acil Durum</option></select></div>
              </div>
              <div className="flex gap-3 pt-4"><Button variant="outline" className="flex-1" onClick={() => setShowAddQuestionModal(false)}>İptal</Button><Button className="flex-1 bg-green-500" onClick={handleAddQuestion}>Ekle</Button></div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Question Modal */}
      {showEditQuestionModal && editingQuestion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl"><CardHeader><CardTitle><Edit className="w-5 h-5 inline mr-2 text-blue-500" />Soru Düzenle</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><label className="text-sm font-medium">Soru *</label><textarea value={editingQuestion.question_text || ''} onChange={(e) => setEditingQuestion({...editingQuestion, question_text: e.target.value})} className="w-full mt-1 p-3 border rounded-lg min-h-[100px]" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm font-medium">Tip</label><select value={editingQuestion.question_type || 'multiple_choice'} onChange={(e) => setEditingQuestion({...editingQuestion, question_type: e.target.value})} className="w-full mt-1 p-2 border rounded-lg"><option value="multiple_choice">Çoktan Seçmeli</option><option value="true_false">Doğru/Yanlış</option><option value="fill_blank">Boşluk Doldurma</option></select></div>
                <div><label className="text-sm font-medium">Zorluk</label><select value={editingQuestion.difficulty || 'medium'} onChange={(e) => setEditingQuestion({...editingQuestion, difficulty: e.target.value})} className="w-full mt-1 p-2 border rounded-lg"><option value="easy">Kolay</option><option value="medium">Orta</option><option value="hard">Zor</option></select></div>
              </div>
              <div><label className="text-sm font-medium">Şıklar</label><div className="space-y-2 mt-1">{(editingQuestion.options || ['', '', '', '']).map((opt, i) => (<Input key={i} value={opt} onChange={(e) => { const newOpts = [...(editingQuestion.options || ['', '', '', ''])]; newOpts[i] = e.target.value; setEditingQuestion({...editingQuestion, options: newOpts}); }} placeholder={`Şık ${String.fromCharCode(65 + i)}`} />))}</div></div>
              <div><label className="text-sm font-medium">Doğru Cevap *</label><Input value={editingQuestion.correct_answer || ''} onChange={(e) => setEditingQuestion({...editingQuestion, correct_answer: e.target.value})} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm font-medium">Kategori</label><select value={editingQuestion.category || 'grammar'} onChange={(e) => setEditingQuestion({...editingQuestion, category: e.target.value})} className="w-full mt-1 p-2 border rounded-lg"><option value="grammar">Gramer</option><option value="vocabulary">Kelime Bilgisi</option><option value="reading">Okuma Anlama</option><option value="sentence completion">Cümle Tamamlama</option><option value="interview">Mülakat</option><option value="emergency">Acil Durum</option><option value="icao">ICAO</option><option value="announcements">Anonslar</option></select></div>
                <div><label className="text-sm font-medium">Modül</label><select value={editingQuestion.module || 'comprehensive'} onChange={(e) => setEditingQuestion({...editingQuestion, module: e.target.value})} className="w-full mt-1 p-2 border rounded-lg"><option value="comprehensive">64 Soruluk</option><option value="flashcards">Flashcard</option><option value="interview">Mülakat</option><option value="emergency">Acil Durum</option></select></div>
              </div>
              <div className="flex gap-3 pt-4"><Button variant="outline" className="flex-1" onClick={() => { setShowEditQuestionModal(false); setEditingQuestion(null); }}>İptal</Button><Button className="flex-1 bg-blue-500" onClick={handleEditQuestion}>Güncelle</Button></div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
